import { useState } from "react"
import { ConsumptionData, ConsumptionErrorResponseData } from "../types/consumption.type"
import { ConsumptionValidationSchema, createSchema } from "../schemas/create.schema"
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import useCreateConsumption from "./useCreateConsumption"

export const useConsumptionForm = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [modalPasswordIsOpen, setModalPasswordIsOpen] = useState(false)

  const [modalSuccessData, setModalSuccessData] = useState<ConsumptionData | boolean>(false)
  const [modalErrorData, setModalErrorData] = useState<ConsumptionErrorResponseData | boolean>(false)

  const formMethods = useForm<ConsumptionValidationSchema>({
    resolver: zodResolver(createSchema),
  })

  const { fields: lineItemsFields, remove: removeLineItem } = useFieldArray({
    control: formMethods.control,
    name: 'line_items_attributes',
  })

  const {
    mutate: createConsumption,
    isPending: createConsumptionIsPending
  } = useCreateConsumption({
    onSuccess: (response) => {
      setModalSuccessData(response.data.data)
    },
    onError: (response) => {
      formMethods.setValue('consumption_password', '')
      setModalErrorData(response)
    },
  })

  const onSubmit: SubmitHandler<ConsumptionValidationSchema> = async (data) => {
    const isValid = await formMethods.trigger()

    if (!isValid) return

    return createConsumption(data)
  }

  const clearForm = (exceptCardIdentifier?: boolean) => {
    if (exceptCardIdentifier) {
      const cardIdentifier = formMethods.getValues('card_identifier');

      formMethods.reset({
        card_identifier: cardIdentifier,
      });
    } else {
      formMethods.reset()
      formMethods.setValue('card_identifier', '')
    }

    removeLineItem([...Array(lineItemsFields.length).keys()])
    setActiveTab(0)
  }

  const handleTabChange = async (tabIndex: number) => {
    if (tabIndex > activeTab) {
      const isValid = await formMethods.trigger(['card_identifier', 'card_id', 'buyer_cpf', 'line_items_attributes'])

      if (!isValid) return
    }

    setActiveTab(tabIndex)
  }

  return {
    createConsumption,
    createConsumptionIsPending,
    onSubmit,
    clearForm,
    handleTabChange,
    modalPasswordIsOpen,
    setModalPasswordIsOpen,
    modalSuccessData,
    setModalSuccessData,
    modalErrorData,
    setModalErrorData,
    formMethods,
    activeTab,
    lineItemsFields,
    removeLineItem,
  }
}
